import React, { useEffect, useState, useCallback } from 'react';
import { buildUrl } from 'utils';
import Layout from 'components/Layout';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import i18n from 'i18n-js';
import VersionModal from 'components/Documents/VersionModal';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentsActions } from 'state/modules/documents';
import VersionDetailsWidget from 'components/Documents/VersionDetailsWidget';
import Logs from 'components/Logs';
import Comments from 'components/Comments';
import { useConfirm } from 'hooks';
import { removeEmpty, transformAsyncErrors } from 'utils/utils';
import DocumentDetailsWidget from 'components/Documents/DocumentDetailsWidget';

function VersionsDetailsPage() {
  const { id, documentId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [modalMode, setModalMode] = useState('idle');
  const [asyncErrors, setAsyncErrors] = useState({});

  // TODO: use the new separeted schema
  const version = useSelector((state) => state.documents?.versions?.current || {});
  const document = useSelector((state) => state.documents.byId[documentId] || {});
  const isUploading = useSelector((state) => state.documents.isUploading);

  const [toggleDeleteVersion, DeleteVersionConfirm] = useConfirm({
    open: false,
    title: i18n.t('documents.delete_version'),
    message: i18n.t('documents.delete_version_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: useCallback(
      () =>
        dispatch(
          DocumentsActions.deleteVersion(documentId, id, () =>
            history.replace(buildUrl(`/documents/${documentId}`))
          )
        ),
      [dispatch, documentId, history, id]
    ),
  });

  const onCreate = async (data) => {
    const response = await dispatch(DocumentsActions.createVersion(documentId, data));
    if (response.ok) {
      setModalMode('idle');
      history.replace(buildUrl(`/documents/${documentId}/versions/${response.data.id}`));
    } else {
      setAsyncErrors({
        ...asyncErrors,
        createVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  const onUpdate = async (data) => {
    const response = dispatch(
      DocumentsActions.updateVersion(
        documentId,
        id,
        data,
        () => {
          if (modalMode !== 'idle') {
            setModalMode('idle');
          }
        },
        true
      )
    );
    if (response.ok && modalMode !== 'idle') {
      setModalMode('idle');
    } else {
      setAsyncErrors({
        ...asyncErrors,
        updateVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  const onRemoveFile = (versionId) => {
    dispatch(DocumentsActions.deleteVersionsFile(documentId, versionId));
  };

  const onUploadFile = (versionId, file) => {
    dispatch(DocumentsActions.uploadVersionsFile(documentId, versionId, file));
  };

  const closeVersionModal = () => {
    setModalMode('idle');
    setAsyncErrors({});
  };

  useEffect(() => {
    dispatch(DocumentsActions.fetchDocument(documentId));
    dispatch(DocumentsActions.fetchVersion(documentId, id));
  }, [dispatch, documentId, id]);

  return (
    <Layout title={i18n.t('documents.version_details')}>
      <DeleteVersionConfirm />
      {modalMode !== 'idle' ? (
        <VersionModal
          open={!!modalMode}
          mode={modalMode}
          asyncErrors={
            asyncErrors === 'update' ? asyncErrors.updateVersion : asyncErrors.createVersion
          }
          handleClose={closeVersionModal}
          handleCreate={modalMode === 'update' ? onUpdate : onCreate}
          {...(modalMode === 'update' && removeEmpty(version))}
        />
      ) : null}
      <Box mb={6} mt={1.75} display="flex" flexDirection="row" justifyContent="space-between">
        <Typography sx={{ fontWeight: 'bold' }} variant="h5">
          {`${i18n.t('documents.version_details')}${version.id ? ` - ${version.version}` : ''}`}
        </Typography>
        {version.id && (
          <Box display="flex" flexWrap="wrap">
            <Button
              sx={{ mb: 1, ml: 2, mt: 1, textTransform: 'none' }}
              variant="outlined"
              color="primary"
              onClick={() => setModalMode('create')}
            >
              Create new version
            </Button>
            <Button
              sx={{ mb: 1, ml: 2, mt: 1, textTransform: 'none' }}
              variant="outlined"
              color="primary"
              onClick={() => setModalMode('update')}
            >
              Edit version
            </Button>
            {!version.default && (
              <Button
                onClick={toggleDeleteVersion}
                sx={{ mb: 1, ml: 2, mt: 1, textTransform: 'none' }}
                variant="contained"
                color="primary"
              >
                Delete version
              </Button>
            )}
          </Box>
        )}
      </Box>
      {version?.id ? (
        <Grid container spacing={3}>
          <Grid item size={{ sm: 12, md: 6, lg: 4 }}>
            <VersionDetailsWidget
              id={id}
              document={documentId}
              handleChange={onUpdate}
              handleRemoveFile={onRemoveFile}
              handleUploadFile={onUploadFile}
              isUploading={isUploading}
              showActions
              showDetailsBtn={false}
            />
            <Box mt={3}></Box>
            <DocumentDetailsWidget document={document} />
          </Grid>
          <Grid item size={{ sm: 12, md: 6, lg: 4 }}>
            <Comments id={parseInt(id)} type="document_version" />
          </Grid>
          <Grid item size={{ sm: 12, md: 6, lg: 4 }}>
            <Logs
              targetId={parseInt(id)}
              targetType="DocumentVersion"
              title="Version logs"
              height={600}
            />
          </Grid>
        </Grid>
      ) : null}
    </Layout>
  );
}

export default VersionsDetailsPage;
